import { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import { weekIdToTimestamp } from 'src/components/BeaconV2Components/SplineChartV2/splineChartV2Utils';
import { quoteWrap } from 'src/utils/stringFormatting';
import { StacklineDate } from 'stackline-datetime';

/**
 * Formats a weekId to a human-readable week ending date string
 */
const formatWeekEnding = (weekId: number): string => {
  const timestamp = weekIdToTimestamp(weekId);
  const date = new Date(timestamp);
  const { year, week } = new StacklineDate(date).getSDSWeekYear();
  const stacklineDate = StacklineDate.getEndDateSDS({ year, week });
  return stacklineDate.toFormat('MMMM d, yyyy');
};

/**
 * Converts SplineChartV2 series data to CSV format
 * 
 * @param primarySeries - Primary series data
 * @param comparisonSeries - Comparison series data (optional)
 * @param primaryDisplayName - Display name for primary series column
 * @param comparisonDisplayName - Display name for comparison series column
 * @returns CSV string
 */
const convertSplineChartV2SeriesToDelimitedData = (
  primarySeries: SplineChartV2Series,
  comparisonSeries?: SplineChartV2Series,
  primaryDisplayName = 'Primary',
  comparisonDisplayName = 'Comparison'
): string => {
  // Build headers
  const headers = [
    'WeekEnding',
    quoteWrap(primaryDisplayName),
    ...(comparisonSeries ? [quoteWrap(comparisonDisplayName)] : [])
  ];

  // Collect all unique weekIds
  const weekIds = new Set<number>();
  primarySeries.data.forEach((point) => weekIds.add(point.weekId));
  comparisonSeries?.data.forEach((point) => weekIds.add(point.weekId));

  // Sort weekIds ascending
  const sortedWeekIds = Array.from(weekIds).sort((a, b) => a - b);

  // Build lookup maps for quick value access
  const primaryMap = new Map(primarySeries.data.map((p) => [p.weekId, p.value]));
  const comparisonMap = comparisonSeries
    ? new Map(comparisonSeries.data.map((p) => [p.weekId, p.value]))
    : null;

  // Build data rows
  const dataRows = sortedWeekIds.map((weekId) => {
    const weekEnding = quoteWrap(formatWeekEnding(weekId));
    const primaryValue = primaryMap.get(weekId) ?? '';
    const comparisonValue = comparisonMap?.get(weekId) ?? '';

    return comparisonSeries
      ? [weekEnding, primaryValue, comparisonValue]
      : [weekEnding, primaryValue];
  });

  // Combine headers and data into CSV
  const csvContent = [headers, ...dataRows].map((row) => row.join(',')).join('\n');

  return `${csvContent}\n`;
};

export default convertSplineChartV2SeriesToDelimitedData;
